import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {axiosInstance} from '../routes/axiosInstance';
import {handleThunkError} from './userSlice';

export const fetchPaymentGroups = createAsyncThunk(
  'paymentGroups/fetch',
  async (_, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.get('/user/payment-group/');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch payment groups.'));
    }
  });

export const createPaymentGroup = createAsyncThunk(
  'paymentGroups/create',
  async ({owner, label, cc_email}, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.post('/user/payment-group/',
        {owner, label, cc_email: cc_email || ''});
      return response.data.data;
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to create payment group.'));
    }
  });

export const updatePaymentGroup = createAsyncThunk(
  'paymentGroups/update',
  async ({id, label, cc_email, min_payout_threshold}, {rejectWithValue}) => {
    try {
      const body = {};
      if (label !== undefined) body.label = label;
      if (cc_email !== undefined) body.cc_email = cc_email;
      if (min_payout_threshold !== undefined) {
        body.min_payout_threshold = min_payout_threshold;
      }
      const response = await axiosInstance.patch(`/user/payment-group/${id}`, body);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to update payment group.'));
    }
  });

export const deletePaymentGroup = createAsyncThunk(
  'paymentGroups/delete',
  async (id, {rejectWithValue}) => {
    try {
      await axiosInstance.delete(`/user/payment-group/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to delete payment group.'));
    }
  });

export const setResourceGroup = createAsyncThunk(
  'paymentGroups/setResourceGroup',
  async ({resourceId, groupId}, {rejectWithValue}) => {
    try {
      await axiosInstance.patch(`/user/resource/${resourceId}`,
        {payment_group: groupId});
      return {resourceId, groupId};
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to update resource group.'));
    }
  });

export const fetchEmailLog = createAsyncThunk(
  'paymentGroups/fetchEmailLog',
  async ({year, month} = {}, {rejectWithValue}) => {
    try {
      let url = '/user/email-log/';
      const params = [];
      if (year) params.push(`year=${year}`);
      if (month) params.push(`month=${month}`);
      if (params.length) url += `?${params.join('&')}`;
      const response = await axiosInstance.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch email log.'));
    }
  });

const initialState = {
  groups: {status: 'idle', data: []},
  emailLog: {status: 'idle', data: []},
};

const paymentGroupsSlice = createSlice({
  name: 'paymentGroups',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentGroups.pending, (state) => {
        state.groups.status = 'loading';
      })
      .addCase(fetchPaymentGroups.fulfilled, (state, action) => {
        state.groups.status = 'succeeded';
        state.groups.data = action.payload || [];
      })
      .addCase(fetchPaymentGroups.rejected, (state) => {
        state.groups.status = 'failed';
      })
      .addCase(createPaymentGroup.fulfilled, (state, action) => {
        if (action.payload) state.groups.data.push(action.payload);
      })
      .addCase(updatePaymentGroup.fulfilled, (state, action) => {
        const updated = action.payload;
        if (!updated) return;
        const index = state.groups.data.findIndex((g) => g.id === updated.id);
        if (index !== -1) state.groups.data[index] = updated;
      })
      .addCase(deletePaymentGroup.fulfilled, (state, action) => {
        state.groups.data = state.groups.data.filter((g) => g.id !== action.payload);
      })
      .addCase(fetchEmailLog.pending, (state) => {
        state.emailLog.status = 'loading';
      })
      .addCase(fetchEmailLog.fulfilled, (state, action) => {
        state.emailLog.status = 'succeeded';
        state.emailLog.data = action.payload || [];
      })
      .addCase(fetchEmailLog.rejected, (state) => {
        state.emailLog.status = 'failed';
      });
  },
});

export default paymentGroupsSlice.reducer;
